import axios from 'axios';
import type { User, Institution, EmailVerification, PrincipalInvestigator, Rating } from '../types';

// Function to get CSRF token from cookies
const getCsrfToken = (): string | null => {
  const name = 'csrftoken';
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()?.split(';').shift() || null;
  return null;
};

// Create axios instance with base URL and CSRF settings
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8000',
  withCredentials: true,
});

// Add request interceptor to include CSRF token
api.interceptors.request.use((config) => {
  const csrfToken = getCsrfToken();
  if (csrfToken) {
    config.headers['X-CSRFToken'] = csrfToken;
  }
  return config;
});

// Authentication Services
export const AuthService = {
  // Request email verification
  requestVerification: async (email: string): Promise<{ verification_id: string; expires_at: string }> => {
    const response = await api.post('/auth/request-verification/', { email });
    return response.data;
  },

  // Verify email with code
  verifyEmail: async (email: string, code: string): Promise<{ verification_id: string }> => {
    const response = await api.post('/auth/verify-email/', { email, code });
    return response.data;
  },

  // Register anonymous user
  register: async (verification_id: string): Promise<User> => {
    const response = await api.post('/auth/register/', { verification_id });
    return response.data;
  },

  // Get current user info
  getCurrentUser: async (): Promise<User> => {
    const response = await api.get('/auth/me/');
    return response.data;
  },

  // Logout user
  logout: async (): Promise<void> => {
    await api.post('/auth/logout/');
  },

  // Check if user is authenticated
  isAuthenticated: async (): Promise<boolean> => {
    try {
      await AuthService.getCurrentUser();
      return true;
    } catch (error) {
      return false;
    }
  }
};

// Institution Services
export const InstitutionService = {
  // Get all institutions
  getAll: async (): Promise<Institution[]> => {
    const response = await api.get('/institutions/');
    return response.data.results;
  },

  // Search institutions
  search: async (query: string): Promise<Institution[]> => {
    const response = await api.get(`/institutions/?search=${query}`);
    return response.data.results;
  },

  // Get institution by ID
  getById: async (id: string): Promise<Institution> => {
    const response = await api.get(`/institutions/${id}/`);
    return response.data;
  }
};

// Principal Investigator Services
export const PIService = {
  // Get all PIs
  getAll: async (page = 1): Promise<{ results: PrincipalInvestigator[]; count: number }> => {
    const response = await api.get(`/investigators/?page=${page}`);
    return response.data;
  },

  // Search PIs
  search: async (query: string): Promise<PrincipalInvestigator[]> => {
    const response = await api.get(`/investigators/?search=${query}`);
    return response.data.results;
  },

  // Get PI by ID
  getById: async (id: string): Promise<PrincipalInvestigator> => {
    const response = await api.get(`/investigators/${id}/`);
    return response.data;
  },

  // Get ratings for a PI
  getRatings: async (id: string): Promise<Rating[]> => {
    const response = await api.get(`/investigators/${id}/ratings/`);
    return response.data.results;
  },

  // Create a new PI
  create: async (data: Partial<PrincipalInvestigator>): Promise<PrincipalInvestigator> => {
    const response = await api.post('/investigators/', data);
    return response.data;
  }
};

// Rating Services
export const RatingService = {
  // Get user's ratings
  getUserRatings: async (): Promise<Rating[]> => {
    const response = await api.get('/ratings/');
    return response.data.results;
  },

  // Submit a rating
  submitRating: async (rating: {
    principal_investigator: string;
    lab_management_score: number;
    interpersonal_score: number;
    comment?: string;
  }): Promise<Rating> => {
    const response = await api.post('/ratings/', rating);
    return response.data;
  },

  // Update a rating
  updateRating: async (id: string, rating: {
    lab_management_score?: number;
    interpersonal_score?: number;
    comment?: string;
  }): Promise<Rating> => {
    const response = await api.patch(`/ratings/${id}/`, rating);
    return response.data;
  }
};

export default api; 